package com.zq.webproject.ui.ranking


import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.zq.webproject.model.RankingUser
import com.zq.webproject.network.Repository
import com.zq.webproject.utils.TokenManager
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch

/**
 * 收入排行榜 ViewModel
 */
class RankingViewModel {
    private val repository = Repository()
    private val coroutineScope = CoroutineScope(Dispatchers.Main)

    // 排行榜数据
    var rankingList by mutableStateOf<List<RankingUser>>(emptyList())
        private set

    // 加载状态
    var isLoading by mutableStateOf(false)
        private set

    // 错误信息
    var errorMessage by mutableStateOf<String?>(null)
        private set

    // 我的排名（从username判断）
    var myRank by mutableStateOf<Int?>(null)
        private set

    // 我的收入（从接口单独获取）
    var myIncome by mutableStateOf<Double?>(null)
        private set

    // 当前登录用户名
    private var currentUsername: String? = null

    init {
        println("RankingViewModel - 初始化")
        currentUsername = TokenManager.getUsername()
        println("RankingViewModel - 当前用户: $currentUsername")
    }

    /**
     * 加载排行榜数据
     */
    fun loadRanking() {
        println("RankingViewModel - 开始加载排行榜数据")

        val token = TokenManager.getToken()
        if (token.isNullOrEmpty()) {
            println("RankingViewModel - Token为空，无法加载排行榜")
            errorMessage = "未登录"
            return
        }

        isLoading = true
        errorMessage = null

        coroutineScope.launch {
            try {
                println("RankingViewModel - 调用 Repository.getIncomeRanking()")
                val result = repository.getIncomeRanking(token)

                result.onSuccess { response ->
                    println("RankingViewModel - 排行榜接口调用成功")
                    println("RankingViewModel - code = ${response.code}")
                    println("RankingViewModel - message = ${response.message}")
                    println("RankingViewModel - data size = ${response.data?.size ?: 0}")

                    if (response.code == 200 && response.data != null) {
                        rankingList = response.data
                        println("RankingViewModel - 排行榜数据加载成功，共 ${rankingList.size} 条")

                        // 打印前3条数据
                        rankingList.take(3).forEachIndexed { index, user ->
                            println("RankingViewModel - 第${index + 1}名: ${user.username}, 收入: ${user.totalIncome}")
                        }

                        // 查找我的排名
                        findMyRank()
                    } else {
                        errorMessage = response.message ?: "排行榜数据为空"
                        println("RankingViewModel - $errorMessage")
                    }
                }.onFailure { e ->
                    errorMessage = "加载失败: ${e.message}"
                    println("RankingViewModel - 排行榜接口调用失败: ${e.message}")
                    e.printStackTrace()
                }
            } catch (e: Exception) {
                errorMessage = "加载失败: ${e.message}"
                println("RankingViewModel - 排行榜加载异常: ${e.message}")
                e.printStackTrace()
            } finally {
                isLoading = false
                println("RankingViewModel - 加载完成，isLoading = false")
            }
        }
    }

    /**
     * 加载我的收入（单独调用接口）
     */
    fun loadMyIncome() {
        println("RankingViewModel - 开始加载我的收入")

        val token = TokenManager.getToken()
        if (token.isNullOrEmpty()) {
            println("RankingViewModel - Token为空，无法加载收入")
            return
        }

        coroutineScope.launch {
            try {
                println("RankingViewModel - 调用 Repository.getUserEarnings()")
                val result = repository.getUserEarnings(token)

                result.onSuccess { response ->
                    println("RankingViewModel - 收入接口调用成功")
                    println("RankingViewModel - code = ${response.code}")
                    println("RankingViewModel - message = ${response.message}")

                    if (response.code == 200 && response.data != null) {
                        myIncome = response.data.totalEarnings
                        println("RankingViewModel - 我的收入加载成功: $myIncome")
                    } else {
                        println("RankingViewModel - 获取收入失败: ${response.message}")
                        // 如果接口失败，设为 0.0
                        myIncome = 0.0
                    }
                }.onFailure { e ->
                    println("RankingViewModel - 收入接口调用失败: ${e.message}")
                    e.printStackTrace()
                    // 如果接口失败，设为 0.0
                    myIncome = 0.0
                }
            } catch (e: Exception) {
                println("RankingViewModel - 加载收入异常: ${e.message}")
                e.printStackTrace()
                // 如果异常，设为 0.0
                myIncome = 0.0
            }
        }
    }

    /**
     * 查找我的排名
     */
    private fun findMyRank() {
        if (currentUsername.isNullOrEmpty()) {
            println("RankingViewModel - 当前用户名为空，无法查找排名")
            return
        }

        val index = rankingList.indexOfFirst { it.username == currentUsername }
        myRank = if (index >= 0) {
            index + 1  // 排名从1开始
        } else {
            null  // 未上榜
        }

        println("RankingViewModel - 我的排名: ${myRank ?: "未上榜"}")
    }

    /**
     * 获取指定排名的用户
     */
    fun getUserAt(rank: Int): RankingUser? {
        return rankingList.getOrNull(rank - 1)
    }

    /**
     * 计算超越百分比
     */
    fun getPercentile(): Int? {
        if (myRank == null || rankingList.isEmpty()) return null
        val totalUsers = rankingList.size
        val percentile = ((totalUsers - myRank!!) * 100.0 / totalUsers).toInt()
        return percentile.coerceIn(0, 100)
    }
}